import type { RouteRecordRaw } from 'vue-router'

import { keys } from '@/app/i18n'

/**
 * The candidates module's routes (wp/candidates §3, manifest rows of WP-12; paths and names from the manifest, D-15 —
 * the legacy paths redirect here through `src/app/router/legacy-redirects.ts`). The board's legacy `:stage` segment is
 * the `?stage=` query. One form view serves the four candidate-form routes (§1.2): they differ only in title,
 * breadcrumb, highlighted nav item and the ACL the guard checks (D-21, the manifest's meta: `cv` read for the CV edit
 * route as legacy had no `mode` there, write for the three others).
 */
const applicationsCrumb = { text: keys.t('candidates.edit.title.applications'), to: { name: 'applications' } }
const cvDatabaseCrumb = { text: keys.t('candidates.edit.title.cvDatabase'), to: { name: 'cvs' } }
const CandidateEditView = () => import('./views/CandidateEditView.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/re/applications',
    name: 'applications',
    component: () => import('./views/ApplicationsBoardView.vue'),
    meta: {
      software: 're',
      resource: 'applications',
      navActiveLink: 'applications',
      // Legacy: page title "Application", breadcrumb "Applications" (`recruiting.js:148-163`).
      pageTitle: keys.t('candidates.board.title.page'),
      breadcrumb: [{ text: keys.t('candidates.board.title.applications'), active: true }],
    },
  },
  {
    path: '/re/applications/:id(\\d+)',
    name: 'view-application',
    component: () => import('./views/ApplicationDetailView.vue'),
    meta: {
      software: 're',
      resource: 'applications',
      navActiveLink: 'applications',
      // The effective legacy record is `upper_common.js:118-138` (it shadows the `recruiting.js` one, §1.2).
      pageTitle: keys.t('candidates.detail.title.page'),
      breadcrumb: [
        { text: keys.t('candidates.detail.title.applicationList'), to: { name: 'applications' } },
        { text: keys.t('candidates.detail.title.application'), active: true },
      ],
    },
  },
  {
    path: '/re/applications/new',
    name: 'create-applications',
    component: CandidateEditView,
    meta: {
      software: 're',
      resource: 'applications',
      action: 'write',
      navActiveLink: 'applications',
      pageTitle: keys.t('candidates.edit.title.application'),
      breadcrumb: [applicationsCrumb, { text: keys.t('candidates.edit.title.application'), active: true }],
    },
  },
  {
    path: '/re/applications/:id(\\d+)/edit',
    name: 'edit-application',
    component: CandidateEditView,
    meta: {
      software: 're',
      resource: 'applications',
      action: 'write',
      navActiveLink: 'applications',
      pageTitle: keys.t('candidates.edit.title.application'),
      breadcrumb: [applicationsCrumb, { text: keys.t('candidates.edit.title.application'), active: true }],
    },
  },
  {
    path: '/re/cvs/new',
    name: 'create-cv',
    component: CandidateEditView,
    meta: {
      software: 're',
      resource: 'cv',
      action: 'write',
      navActiveLink: 'cvs',
      pageTitle: keys.t('candidates.edit.title.createCv'),
      breadcrumb: [cvDatabaseCrumb, { text: keys.t('candidates.edit.title.createCv'), active: true }],
    },
  },
  {
    path: '/re/cvs/:id(\\d+)/edit',
    name: 'cv-edit',
    component: CandidateEditView,
    meta: {
      software: 're',
      resource: 'cv',
      navActiveLink: 'cvs',
      pageTitle: keys.t('candidates.edit.title.editCv'),
      breadcrumb: [cvDatabaseCrumb, { text: keys.t('candidates.edit.title.cvEdit'), active: true }],
    },
  },
]

export default routes
